/**
 * @module ServiceCheckForm
 * @description Service Check Form
 */
import { useState, useRef, useEffect, useContext, useMemo } from "react";
import debounce from "lodash/debounce";
import { usePredictiveSearch } from "@/hooks/usePredictiveSearch";
import { useResumeQuoteAddress } from "@/hooks/useResumeQuoteAddress";
import { pickSuggestionForPrefill } from "@/js/utils/address";
import { isValidAddressInput } from "@/js/utils/validators";
import { stringify } from "@/js/utils/filters";
import { trackEvent } from "@/js/helpers/tracking";
import { v4 as uuidv4 } from "uuid";
import styles from "./index.module.css";
import { FuelContext } from "@/providers/fuel-provider";
import { persistData } from "@/js/utils/session-storage";
import useMediaQuery from "@/hooks/useMediaQuery";
import useBrowser from "@/hooks/useBrowser";
import Loader from "@/blocks/Loader";

const defaultTextDesktop = "Enter your address to view plans in your area";
const defaultTextMobile = "Enter your address";

/**
 * @function Component
 * @description
 */
const Component = ({ ...props }) => {
  const {
    theme,
    isHero,
    customCta,
    reducedCta,
    reducedPlaceholderText,
    editPlaceHolderTextDesktop,
    editPlaceHolderTextMobile,
    enterGDT = false,
    enableNetflixPassthrough = false,
    position = "",
    slideNumber,
    ctaColor,
  } = props;

  const carouselPosition = slideNumber ? `- SLIDE ${slideNumber}` : "";

  const formContext = useMemo(
    () => ({
      formType: "SERVICE CHECK - SINGLE LINE",
      formName: "SERVICE CHECK - SINGLE LINE",
      formPosition: `${position} ${carouselPosition}`,
    }),
    [position, carouselPosition],
  );

  const [addressValid, setAddressValid] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [addressObject, setAddressObject] = useState({});
  const [formStarted, setFormStarted] = useState(false);
  const [addressKey, setAddressKey] = useState(false);
  const [placeholderText, setPlaceholderText] = useState(defaultTextMobile);
  const [id, setId] = useState();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const isXLDesktop = useMediaQuery("(min-width: 1440px)");
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1279px)");
  const addressField = useRef(null);
  const honeyPotField = useRef(null);
  const swiperPaginationElRef = useRef(null);
  const swiperPaginationPrevDisplayRef = useRef(null);

  const { matchDevice: isIOS, matchBrowser: isSafari } = useBrowser(
    "device-ios",
    "browser-safari",
  );

  const predictive = usePredictiveSearch(
    { addressField, honeyPotField },
    setSuggestions,
    formContext,
    enableNetflixPassthrough,
  );
  const { prefillAddress, isPrefillLoading } = useResumeQuoteAddress();

  const { setFormInputted, setPauseCarousel } = useContext(FuelContext);

  /**
   * @function setSuggestionValue
   * @description Set suggestion value on address and zip
   */
  const setSuggestionValue = async (event) => {
    const { address, addressData } = await predictive.setSuggestionValue(event);

    setAddressValid(isValidAddressInput(address));
    setAddressError(!isValidAddressInput(address));
    setAddressObject(addressData);
    setAddressKey(true);
  };

  /**
   * @function fetchAddress
   * @description Fetch our address suggestions
   */
  const fetchAddress = async (event) => {
    if (!formStarted) {
      trackEvent({ action: "formStarted", data: formContext });
      setFormStarted(true);
    }

    const addressSuggestions = await predictive.fetchAddress(event);
    setFormInputted(true);

    return addressSuggestions || [];
  };

  // Store fetchAddress in a ref so debounced references the same version across renders
  const fetchAddressRef = useRef(fetchAddress);
  fetchAddressRef.current = fetchAddress;

  // Store debounced version of fetchAddress in a memo to prevent it from being recreated on every render and preserve the debounce behavior
  const debouncedFetchAddress = useMemo(
    () =>
      debounce(async (target) => {
        const addressSuggestions = await fetchAddressRef.current({ target });

        target.dispatchEvent(
          new CustomEvent("address-input", {
            bubbles: true,
            detail: { suggestions: addressSuggestions.length > 0 },
          }),
        );
      }, 500),
    [],
  );

  // Cleanup debounce on unmount
  useEffect(
    () => () => debouncedFetchAddress.cancel(),
    [debouncedFetchAddress],
  );

  const shouldHideSwiperPagination =
    (isDesktop || isTablet) && suggestions.length > 2;

  useEffect(() => {
    const inputEl = addressField.current;

    if (!inputEl) return;

    const slideEl = inputEl.closest(".swiper-slide");

    if (!slideEl) return;

    const paginationEl =
      swiperPaginationElRef.current ||
      slideEl.querySelector(".swiper-pagination");

    if (!paginationEl) return;

    if (!swiperPaginationElRef.current) {
      swiperPaginationElRef.current = paginationEl;
    }

    if (swiperPaginationPrevDisplayRef.current === null) {
      swiperPaginationPrevDisplayRef.current = paginationEl.style.display;
    }

    paginationEl.style.display = shouldHideSwiperPagination
      ? "none"
      : swiperPaginationPrevDisplayRef.current || "";
  }, [shouldHideSwiperPagination]);

  useEffect(
    () => () => {
      if (swiperPaginationElRef.current) {
        swiperPaginationElRef.current.style.display =
          swiperPaginationPrevDisplayRef.current || "";
      }

      swiperPaginationElRef.current = null;
      swiperPaginationPrevDisplayRef.current = null;
    },
    [],
  );

  /**
   * @function handleResize
   * @description Update placeholder text if viewport changes
   */
  const handleResize = () => {
    if (isDesktop && !reducedPlaceholderText) {
      setPlaceholderText(editPlaceHolderTextDesktop ?? defaultTextDesktop);
    } else {
      setPlaceholderText(editPlaceHolderTextMobile ?? defaultTextMobile);
    }
  };

  useEffect(() => {
    handleResize();
    document.addEventListener("redventures/resize_executed", handleResize);
  }, [
    isDesktop,
    reducedPlaceholderText,
    editPlaceHolderTextDesktop,
    editPlaceHolderTextMobile,
  ]);

  /**
   * @function useEffect
   * @description Set our placeholder, then attach viewport resize event
   */
  useEffect(() => {
    setId(uuidv4());
    persistData("netflixProduct", false);
  }, []);

  /**
   * @function useEffect
   * @description When a resume-quote address is available, pre-fill the address
   * input and fetch predictive suggestions so the user can confirm or modify the
   * address with a single click rather than retyping it.
   */
  useEffect(() => {
    if (!prefillAddress || isPrefillLoading || !addressField.current) return;

    const autoPopulate = async () => {
      const addressSuggestions = await predictive.fetchAddressSuggestions(
        prefillAddress,
        "",
      );

      if (!addressSuggestions || addressSuggestions.length === 0) {
        // Fallback: at least pre-fill the raw text so the user can adjust
        addressField.current.value = prefillAddress;
        return;
      }

      const resolvedMatch = pickSuggestionForPrefill(
        addressSuggestions,
        prefillAddress,
      );

      // Populate the visible input (strip the trailing zip, matching the normal suggestion-click behaviour)
      addressField.current.value = resolvedMatch.value.replace(
        ` ${resolvedMatch.zipCode}`,
        "",
      );

      // Update form state so the submit button becomes active
      setAddressValid(isValidAddressInput(resolvedMatch.value));
      setAddressError(false);
      setAddressObject(resolvedMatch);
      setAddressKey(true);
    };

    autoPopulate();
  }, [prefillAddress, isPrefillLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * @function handleAddressCheckActive
   * @description Set pauseCarousel to true when address check is active
   */
  const handleAddressCheckActive = () => {
    setPauseCarousel(true);
  };

  /**
   * @function handleAddressCheckInactive
   * @description Set pauseCarousel to false when address check is inactive
   */
  const handleAddressCheckInactive = () => {
    // Only unpause if there are no suggestions showing AND the input is not focused
    if (suggestions.length === 0 && !isInputFocused) {
      setPauseCarousel(false);
    }
  };

  /**
   * @function handleOnAddressInput
   * @description Handle address input event
   */
  const handleOnAddressInput = (event) => {
    event.target.dispatchEvent(
      new CustomEvent("address-input", {
        bubbles: true,
        detail: { resetOnly: true },
      }),
    );

    debouncedFetchAddress(event.target);
  };

  /**
   * @function handleInputFocus
   * @description Handle input focus event
   */
  const handleInputFocus = () => {
    setIsInputFocused(true);
    setPauseCarousel(true);
  };

  /**
   * @function handleInputClick
   * @description Handle input click event
   */
  const handleInputClick = () => {
    setIsInputFocused(true);
    setPauseCarousel(true);
  };

  /**
   * @function handleInputBlur
   * @description Handle input blur event
   */
  const handleInputBlur = () => {
    setIsInputFocused(false);
    // Only unpause if there are no suggestions showing
    if (suggestions.length === 0) {
      setPauseCarousel(false);
    }
    if (isIOS && isSafari) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /**
   * @function determineCtaCopy
   * @description Calculate the call-to-action copy based on conditions
   */
  const determineCtaCopy = () => {
    if (customCta) return customCta;
    if (reducedCta) return "Go";
    if (isXLDesktop) return "Check availability";
    return "Go";
  };

  const inputMinWidth = useMemo(() => {
    if (typeof window === "undefined") return 0;

    const ruler = document.createElement("span");
    ruler.textContent = placeholderText;
    ruler.style.visibility = "hidden";
    ruler.style.position = "absolute";
    ruler.style.whiteSpace = "nowrap";
    ruler.style.fontSize = "18px";

    document.body.appendChild(ruler);
    const width = ruler.offsetWidth;
    document.body.removeChild(ruler);

    return width > 20 ? width - 20 : width;
  }, [placeholderText]);

  return (
    <div
      className={`${styles.component} ${theme && styles[`component__${theme}`]} ${isHero ? styles.component__isHero : ""}`}
    >
      <form
        name="service-check-form"
        className={styles.component__form}
        autoComplete="off"
        onSubmit={(event) => predictive.handleSubmit(event, enterGDT)}
        onMouseEnter={handleAddressCheckActive}
        onFocus={handleAddressCheckActive}
        onMouseLeave={handleAddressCheckInactive}
        onBlur={handleAddressCheckInactive}
        data-address-object={stringify(addressObject)}
        data-cypress="service-check-form"
      >
        {isPrefillLoading ? (
          <div className={`${styles.component__loader}`}>{<Loader />} </div>
        ) : (
          <span
            className={`${styles.component__icon} icon icon-location-marker2`}
          />
        )}
        <div className={`field ${styles.component__formField} fs-mask`}>
          <label
            className="screen-reader-text"
            htmlFor={`street-address-${id}`}
          >
            Street Address
          </label>
          <input
            ref={addressField}
            type="text"
            name="street-address"
            id={`street-address-${id}`}
            data-test-id="street-address"
            onInput={handleOnAddressInput} /* fetchAddress occurs here */
            onFocus={handleInputFocus}
            onClick={handleInputClick}
            onBlur={handleInputBlur}
            className="input"
            style={{ minWidth: inputMinWidth }}
            placeholder={isPrefillLoading ? "" : placeholderText}
            readOnly={isPrefillLoading}
            data-payload={stringify({
              fieldName: "street",
              fieldType: "street",
              fieldValue: "STREET ADDRESS",
            })}
          />
          <span
            className={`field-error ${addressError === true ? "--active" : ""}`}
          >
            Invalid Address
          </span>
          <div
            className={`${styles.component__suggestions} ${suggestions && suggestions.length > 0 ? styles.component__suggestionsActive : ""}`}
          >
            {suggestions && suggestions.length > 0 && (
              <>
                <ul className={styles.component__suggestionsList}>
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        role="button"
                        data-address={suggestion.value}
                        data-zip={suggestion.zipCode}
                        data-object={stringify(suggestion)}
                        onClick={setSuggestionValue}
                        onMouseEnter={handleAddressCheckActive}
                      >
                        {suggestion.value}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className={styles.component__manual}>
                  <button
                    type="button"
                    role="button"
                    onClick={() => {
                      setSuggestions([]);
                      setPauseCarousel(false);
                    }}
                  >
                    Close address suggestions
                  </button>
                </div>
              </>
            )}
          </div>
          {/* <!-- .component__suggestions --> */}
        </div>
        {/* <!-- .field --> */}

        <div className="field" data-hidden-input>
          <label htmlFor={`hidden-input-${id}`}>Hidden Input</label>
          <input
            ref={honeyPotField}
            type="text"
            name="hidden-input"
            id={`hidden-input-${id}`}
            onInput={predictive.handleInput}
            data-payload={stringify({
              fieldName: "honeypot",
              fieldType: "honeypot",
              fieldValue: "HONEYPOT",
            })}
          />
        </div>
        {/* <!-- .field --> */}

        <button
          type="submit"
          className={`btn ${ctaColor === "black" ? styles.component__blackCTA : ""}`}
          disabled={!addressValid || !addressKey}
          aria-disabled={!addressValid || !addressKey}
          role="button"
        >
          {determineCtaCopy()}
        </button>
      </form>
    </div>
  );
};

export default Component;
